import { useEffect, useMemo, useState } from 'react'

export interface TimeAgoProps {
  /**
   * ISO datetime string or Date instance.
   */
  date?: string | Date
  /**
   * Update interval in ms. Defaults to 30s.
   */
  refreshMs?: number
  className?: string
  /**
   * When date is missing/invalid.
   */
  emptyText?: string
}

const toDate = (input: TimeAgoProps['date']): Date | undefined => {
  if (!input) return
  if (input instanceof Date) return Number.isFinite(input.getTime()) ? input : undefined
  const d = new Date(input)
  return Number.isFinite(d.getTime()) ? d : undefined
}

const formatRelative = (target: Date, now: Date): string => {
  const diffMs = target.getTime() - now.getTime()
  const diffSec = Math.round(diffMs / 1000)

  const abs = Math.abs(diffSec)
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

  if (abs < 45) return rtf.format(diffSec, 'second')

  const diffMin = Math.round(diffSec / 60)
  if (Math.abs(diffMin) < 45) return rtf.format(diffMin, 'minute')

  const diffHr = Math.round(diffMin / 60)
  if (Math.abs(diffHr) < 22) return rtf.format(diffHr, 'hour')

  const diffDay = Math.round(diffHr / 24)
  if (Math.abs(diffDay) < 26) return rtf.format(diffDay, 'day')

  const diffMonth = Math.round(diffDay / 30)
  if (Math.abs(diffMonth) < 11) return rtf.format(diffMonth, 'month')

  const diffYear = Math.round(diffDay / 365)
  return rtf.format(diffYear, 'year')
}

export const TimeAgo = ({ date, refreshMs = 30_000, emptyText = '' }: TimeAgoProps) => {
  const target = useMemo(() => toDate(date), [date])
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    if (!target) return
    const id = window.setInterval(() => setNow(new Date()), refreshMs)
    return () => window.clearInterval(id)
  }, [target, refreshMs])

  if (!target) {
    return emptyText ? <>{emptyText}</> : null
  }

  return <>{formatRelative(target, now)}</>
}
